import type { PerformanceTier } from '@/device/deviceConfig'
import type { AmznStreamingOptions } from '@amzn/vinyl-amzn'
import type { SegmentControllerImplOptions, TrackControllerImplOptions } from '@amazon/vinyl'

export interface PerformanceProfile {
    /** Vinyl `VinylDependencyOptions.trackController`. */
    readonly trackController: Pick<
        TrackControllerImplOptions,
        'trackPrefetchCount' | 'preloadCapacity'
    >
    /** Vinyl `VinylDependencyOptions.streaming.segmentController`, in seconds. */
    readonly segmentController: SegmentControllerImplOptions
    /** Vinyl `AudioOptions.maxSampleRate` in Hz; null leaves the platform's reported rate. */
    readonly maxSampleRate: number | null
    readonly uhdAllowed: boolean
    readonly reduceMotionByDefault: boolean
}

export const CONSTRAINED_MAX_SAMPLE_RATE = 48000

const high: PerformanceProfile = {
    trackController: { trackPrefetchCount: 2, preloadCapacity: 2 },
    segmentController: { prefetchActive: 180, prefetchInactive: 20, retainTail: 30 },
    maxSampleRate: null,
    uhdAllowed: true,
    reduceMotionByDefault: false,
}

const medium: PerformanceProfile = {
    trackController: { trackPrefetchCount: 1, preloadCapacity: 1 },
    segmentController: { prefetchActive: 60, prefetchInactive: 10, retainTail: 15 },
    maxSampleRate: CONSTRAINED_MAX_SAMPLE_RATE,
    uhdAllowed: false,
    reduceMotionByDefault: false,
}

const low: PerformanceProfile = {
    trackController: { trackPrefetchCount: 1, preloadCapacity: 1 },
    segmentController: { prefetchActive: 30, prefetchInactive: 5, retainTail: 5 },
    maxSampleRate: CONSTRAINED_MAX_SAMPLE_RATE,
    uhdAllowed: false,
    reduceMotionByDefault: true,
}

const profiles = { high, medium, low } as const satisfies Record<
    PerformanceTier,
    PerformanceProfile
>

export function performanceProfile(tier: PerformanceTier): PerformanceProfile {
    return profiles[tier]
}

export function constrainStreamingOptions(
    options: AmznStreamingOptions,
    tier: PerformanceTier
): AmznStreamingOptions {
    return performanceProfile(tier).uhdAllowed || !options.uhdEnabled
        ? options
        : { ...options, uhdEnabled: false }
}
